import { useEffect, useRef, useState } from "react";
import anime from "animejs"; // v3.2.1
import {
  Facebook,
  Linkedin,
  Instagram,
  Twitter,
  Mail,
  Phone,
  MapPin,
  Clock,
  ArrowRight,
  ShieldCheck,
} from "lucide-react";
import { Link } from "react-router-dom";

export default function Footer() {
  const [email, setEmail] = useState("");
  const barRef = useRef(null);
  const socialRefs = useRef([]);

  useEffect(() => {
    // Subtle animated gradient bar
    const bar = barRef.current;
    if (bar) {
      anime({
        targets: bar,
        translateX: ["-10%", "10%"],
        direction: "alternate",
        easing: "easeInOutSine",
        duration: 4000,
        loop: true,
      });
    }
    // Social icons hover micro-bounce
    socialRefs.current.forEach((el) => {
      if (!el) return;
      el.addEventListener("mouseenter", () =>
        anime({
          targets: el,
          translateY: [
            { value: -3, duration: 160 },
            { value: 0, duration: 220 },
          ],
          easing: "easeOutQuad",
        })
      );
    });
  }, []);

  const submitNewsletter = (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    // TODO: integrate with your backend/Email API
    alert(`Thanks! We'll keep you posted at ${email}`);
    setEmail("");
  };

  const year = new Date().getFullYear();

  return (
    <footer className="relative mt-24 bg-gray-950 text-gray-400">
      {/* Animated top accent */}
      <div className="relative h-1 overflow-hidden">
        <div
          ref={barRef}
          className="absolute inset-y-0 left-0 w-[140%] bg-gradient-to-r from-indigo-500 via-purple-600 to-pink-500"
        />
      </div>

      <div className="max-w-7xl mx-auto px-4 lg:px-6 py-14">
        <div className="grid gap-10 lg:gap-12 md:grid-cols-2 lg:grid-cols-4">
          {/* Brand + value */}
          <div>
            <Link to="/" className="inline-flex items-center mb-4">
              <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg grid place-items-center text-white font-bold text-lg shadow-md mr-2">
                PW
              </div>
              <span className="text-2xl font-extrabold text-white">
                Profile<span className="text-indigo-400">Wise</span>
              </span>
            </Link>
            <p className="text-sm leading-relaxed text-gray-400">
              We turn potential into opportunity — ATS-ready resumes and
              recruiter-magnet LinkedIn profiles, backed by keyword strategy and
              clear storytelling.
            </p>

            {/* Trust strip */}
            <div className="mt-5 flex flex-wrap items-center gap-3">
              <span className="inline-flex items-center gap-2 text-xs text-emerald-400 bg-emerald-900/30 border border-emerald-700/30 px-2.5 py-1 rounded-full">
                <ShieldCheck size={14} /> ATS compliant
              </span>
              <span className="inline-flex items-center gap-2 text-xs text-indigo-300 bg-indigo-900/30 border border-indigo-700/30 px-2.5 py-1 rounded-full">
                48 hrs turnaround
              </span>
            </div>
          </div>

          {/* Company */}
          <div>
            <h4 className="text-white font-semibold mb-3">Company</h4>
            <ul className="space-y-2 text-sm">
              <li>
                <Link to="/aboutUs" className="hover:text-white transition">
                  About us
                </Link>
              </li>
              <li>
                <Link to="/pricing" className="hover:text-white transition">
                  Pricing
                </Link>
              </li>
              <li>
                <a href="#testimonials" className="hover:text-white transition">
                  Testimonials
                </a>
              </li>
              <li>
                <Link to="/contactUs" className="hover:text-white transition">
                  Contact
                </Link>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-white font-semibold mb-3">Services</h4>
            <ul className="space-y-2 text-sm">
              <li>
                <a href="#services" className="hover:text-white transition">
                  Profile Optimization
                </a>
              </li>
              <li>
                <Link to="/pricing" className="hover:text-white transition">
                  Resume Alignment + ATS
                </Link>
              </li>
              <li>
                <Link to="/clients" className="hover:text-white transition">
                  All Clients Directory
                </Link>
              </li>
              <li>
                <a href="#how" className="hover:text-white transition">
                  How we help you grow
                </a>
              </li>
            </ul>
          </div>

          {/* Newsletter / Contact */}
          <div>
            <h4 className="text-white font-semibold mb-3">Stay in the loop</h4>
            <p className="text-sm text-gray-400 mb-3">
              Get interview tips, profile templates, and offer updates. No spam.
            </p>
            <form
              onSubmit={submitNewsletter}
              className="flex items-center gap-2 mb-5"
            >
              <div className="relative flex-1">
                <Mail
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500"
                  size={16}
                />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="you@domain.com"
                  className="w-full pl-9 pr-3 py-2 rounded-lg bg-gray-900/60 border border-gray-800 text-gray-200 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <button
                type="submit"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 text-white font-semibold hover:bg-indigo-700 transition"
              >
                Subscribe <ArrowRight size={16} />
              </button>
            </form>

            <ul className="space-y-2 text-sm">
              <li className="flex items-start gap-2">
                <Phone size={16} className="mt-0.5 text-gray-500" />
                <span>+91-7057051648</span>
              </li>
              <li className="flex items-start gap-2">
                <MapPin size={16} className="mt-0.5 text-gray-500" />
                <span>
                  Pune • Hinjawadi Pimpri-Chinchwad • Remote-first (India)
                </span>
              </li>
              <li className="flex items-start gap-2">
                <Clock size={16} className="mt-0.5 text-gray-500" />
                <span>Mon–Sun: 24 Hours</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Divider */}
        <div className="my-8 border-t border-gray-800" />

        {/* Bottom row */}
        <div className="flex flex-col md:flex-row items-center md:items-center justify-between gap-4">
          {/* Socials */}
          <div className="flex items-center gap-4">
            <a
              href="https://wa.me/917057051648"
              target="_blank"
              rel="noreferrer"
              className="p-2 rounded-lg bg-gray-900/60 border border-gray-800 text-gray-300 hover:bg-emerald-600 hover:border-emerald-500/50 transition-all group"
              aria-label="WhatsApp"
            >
              <svg
                width="19"
                height="19"
                viewBox="0 0 19 19"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M15.255 3.713a8 8 0 0 0-5.684-2.36c-4.433 0-8.043 3.603-8.043 8.036 0 1.394.364 2.771 1.045 3.974l-1.164 4.26 4.354-1.14a8.06 8.06 0 0 0 3.8.957c4.434 0 8.044-3.61 8.044-8.043 0-2.145-.84-4.172-2.352-5.692zM4.283 13.11c-.76-.863-1.18-2.312-1.18-3.72a6.467 6.467 0 0 1 6.46-6.46 6.42 6.42 0 0 1 4.568 1.891 6.42 6.42 0 0 1 1.892 4.568 6.467 6.467 0 0 1-6.46 6.46c-1.258 0-2.596-.404-3.562-1.06l-2.343.609z"
                  fill="#fff"
                  style={{ fill: "#fff", fillOpacity: 1 }}
                ></path>
                <path
                  d="M11.748 10.434c.182.064 1.148.539 1.346.641.198.103.333.15.38.23.048.08.048.475-.119.934s-.95.879-1.33.934c-.34.048-.768.072-1.242-.079a12 12 0 0 1-1.125-.412c-1.979-.854-3.27-2.842-3.364-2.976-.103-.143-.8-1.069-.8-2.035s.507-1.448.689-1.646a.72.72 0 0 1 .522-.246h.38c.12 0 .285-.047.444.34.166.396.562 1.362.61 1.465a.38.38 0 0 1 .015.349c-.063.134-.095.213-.198.324a8 8 0 0 1-.293.348c-.095.095-.198.206-.087.404.119.198.507.84 1.093 1.362.752.673 1.385.879 1.583.974s.309.079.428-.048c.118-.135.49-.578.625-.776s.261-.166.443-.095z"
                  fill="#fff"
                  style={{ fill: "#fff", fillOpacity: 1 }}
                ></path>
              </svg>
            </a>
            <a
              href="https://www.linkedin.com/company/profilewise/"
              target="_blank"
              rel="noreferrer"
              ref={(el) => (socialRefs.current[1] = el)}
              className="p-2 rounded-lg bg-gray-900/60 border border-gray-800 text-gray-300 hover:text-white"
              aria-label="LinkedIn"
            >
              <Linkedin size={18} />
            </a>
            {/* <a
              href="https://instagram.com"
              target="_blank"
              rel="noreferrer"
              ref={(el) => (socialRefs.current[2] = el)}
              className="p-2 rounded-lg bg-gray-900/60 border border-gray-800 text-gray-300 hover:text-white"
              aria-label="Instagram"
            >
              <Instagram size={18} />
            </a> */}
            {/* <a
              href="https://twitter.com"
              target="_blank"
              rel="noreferrer"
              ref={(el) => (socialRefs.current[3] = el)}
              className="p-2 rounded-lg bg-gray-900/60 border border-gray-800 text-gray-300 hover:text-white"
              aria-label="Twitter"
            >
              <Twitter size={18} />
            </a> */}
          </div>

          {/* Legal */}
          <div className="text-xs text-gray-500">
            © {year} ProfileWise. All rights reserved.
            <span className="mx-2">•</span>
            <Link to="/terms" className="hover:text-white">
              Terms
            </Link>
            <span className="mx-2">•</span>
            <Link to="/privacy" className="hover:text-white">
              Privacy
            </Link>
            <span className="mx-2">•</span>
            <Link to="/refunds" className="hover:text-white">
              Refunds
            </Link>
          </div>

          {/* Small note */}
          <div className="flex flex-col items-center text-xs text-gray-500 md:items-end">
            <span>Website built by me</span>

            {/* LinkedIn Profile Link */}
            <a
              href="https://www.linkedin.com/in/mohammad-javed-517a991a0/?trk=public-profile-join-page"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 mt-1 text-gray-400 hover:text-white transition"
            >
              <Linkedin size={14} />
              <span>Connect on LinkedIn</span>
            </a>

            <span className="mt-1">Made with care in India 🇮🇳</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
